import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import useScreenWidth from "../../lib/useGetScreenWidth";
import { Plus } from "@phosphor-icons/react";
import { iconSize } from "../../const/sizes";
import { useFormik } from "formik";
import RequiredForm from "../RequiredForm";
import Textarea from "../Textarea";
import cr from "../../lib/request";
import { useNavigate } from "react-router-dom";
import { CourseTema } from "../../const/types";
import ComponentSpinner from "../ComponentSpinner";
import FileUploader from "../FileUploader";
import * as yup from "yup";

export default function TambahCourseModal() {
  const sw = useScreenWidth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();

  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false);

  const [tema, setTema] = useState<CourseTema[] | null>(null);
  const [temaLoading, setTemaLoading] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen && !tema) {
      setTemaLoading(true);
      const url = `api/course/theme/alltheme`;

      cr.get(url)
        .then((r) => {
          if (r.status === 200) {
            setTema(r.data.data);
          }
        })
        .catch((e) => {
          console.log(e);
          toast({
            title: "Maaf Terjadi Kesalahan",
            status: "error",
            duration: 10000,
            isClosable: true,
          });
          navigate("/course/list");
        })
        .finally(() => {
          setTemaLoading(false);
        });
    }
  }, [navigate, toast, isOpen, tema]);

  const formik = useFormik({
    validateOnChange: false,

    initialValues: {
      nama_course: "",
      theme_id: "",
      description: "",
      image: [] as File[] | null,
      materi: [] as File[] | null,
    },

    validationSchema: yup.object().shape({
      nama_course: yup.string().required("Nama Course harus diisi"),
      theme_id: yup.number().required("Tema Course harus dipilih"),
      description: yup.string().required("Deskripsi Course harus diisi"),
      image: yup
        .array()
        .min(1, "Gambar Thumbnail harus diunggah")
        .required("Gambar Thumbnail harus diunggah")
        .test(
          "fileType",
          "Hanya file JPG, PNG, HEIC, WEBP yang diperbolehkan",
          (value) =>
            value.every(
              (file) =>
                file.type === "image/jpeg" ||
                file.type === "image/jpg" ||
                file.type === "image/png" ||
                file.type === "image/HEIC" ||
                file.type === "image/webp"
            )
        ),
      materi: yup
        .array()
        .test(
          "fileType",
          "Hanya file ZIP, ZIPX, CBZ, RAR, 7Z, PDF, PPT, Word yang diperbolehkan",
          (value) => {
            const allowedExtensions =
              /\.(zip|zipx|cbz|rar|7z|pdf|ppt|pptx|doc|docx)$/i;
            return (
              value && value.every((file) => allowedExtensions.test(file.name))
            );
          }
        ),
    }),

    onSubmit: (values, { resetForm }) => {
      console.log(values);

      setLoading(true);
      const url = `api/course/savecourse`;
      const payload = new FormData();
      payload.append("nama_course", values.nama_course);
      payload.append("theme_id", values.theme_id);
      payload.append("description", values.description);
      if (values.image) {
        values.image.forEach((file) => {
          payload.append("image", file);
        });
      }
      if (values.materi) {
        values.materi.forEach((file) => {
          payload.append("materi", file);
        });
      }

      cr.post(url, payload)
        .then((r) => {
          if (r.status === 201) {
            resetForm();
            toast({
              title: r?.data?.message,
              status: "success",
              duration: 5000,
              isClosable: true,
            });
            navigate(`/course/${r?.data?.data?.id}/edit`);
          }
        })
        .catch((e) => {
          console.log(e);
          toast({
            title: "Gagal menambahkan course",
            description: e?.response?.data?.message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  return (
    <>
      {sw >= 500 ? (
        <Button
          colorScheme="p"
          className="clicky"
          leftIcon={<Icon as={Plus} fontSize={iconSize} weight="bold" />}
          pl={3}
          onClick={onOpen}
          flexShrink={0}
        >
          Tambah
        </Button>
      ) : (
        <IconButton
          colorScheme="p"
          className="clicky"
          aria-label="tambahKategoriBlog"
          icon={<Icon as={Plus} fontSize={iconSize} />}
          onClick={onOpen}
        />
      )}

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />

        <ModalContent>
          <ModalHeader>Tambah Course</ModalHeader>

          {!temaLoading && tema ? (
            <>
              <ModalBody>
                <Image
                  src={thumbnailPreviewUrl || "/images/thumbnailDefault.png"}
                  w={"100%"}
                  h={"200px"}
                  objectFit={"cover"}
                  mb={4}
                  border={"1px solid var(--divider3)"}
                />

                <form id="tambahCourseForm" onSubmit={formik.handleSubmit}>
                  <FormControl
                    isInvalid={formik.errors.image ? true : false}
                    mb={4}
                  >
                    <FormLabel>
                      Gambar Thumbnail
                      <RequiredForm />
                    </FormLabel>
                    <FileUploader
                      name="image"
                      formik={formik}
                      setPreviewUrl={setThumbnailPreviewUrl}
                      accept=".jpg, .jpeg, .png, .HEIC, .svg, .webp"
                    />
                    <Text mt={1} fontSize={"sm"}>
                      Optimal aspect ratio 16/9
                    </Text>
                    <FormErrorMessage>{formik.errors.image}</FormErrorMessage>
                  </FormControl>

                  <FormControl
                    mb={4}
                    isInvalid={formik.errors.nama_course ? true : false}
                  >
                    <FormLabel>
                      Nama Course
                      <RequiredForm />
                    </FormLabel>
                    <Textarea
                      name="nama_course"
                      placeholder="Masukkan nama course"
                      formik={formik}
                    />

                    <FormErrorMessage>
                      {formik.errors.nama_course}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl
                    mb={4}
                    isInvalid={formik.errors.theme_id ? true : false}
                  >
                    <FormLabel>
                      Tema
                      <RequiredForm />
                    </FormLabel>
                    <Select
                      name="theme_id"
                      placeholder="Pilih tema course"
                      onChange={formik.handleChange}
                      value={formik.values.theme_id}
                    >
                      {tema.map((t, i) => (
                        <option key={i} value={t.id}>
                          {t.theme_name}
                        </option>
                      ))}
                    </Select>

                    <FormErrorMessage>
                      {formik.errors.theme_id}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isInvalid={formik.errors.description ? true : false}
                    mb={4}
                  >
                    <FormLabel>
                      Deskripsi
                      <RequiredForm />
                    </FormLabel>
                    <Textarea
                      name="description"
                      placeholder="Masukkan deskripsi"
                      formik={formik}
                    />

                    <FormErrorMessage>
                      {formik.errors.description}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={formik.errors.materi ? true : false}>
                    <FormLabel>Materi</FormLabel>
                    <FileUploader
                      name="materi"
                      formik={formik}
                      accept=".zip, .zipx, .cbz, .rar, .7z, .pdf, .doc, .docx, .pptx"
                    />
                    <FormErrorMessage>{formik.errors.materi}</FormErrorMessage>
                  </FormControl>
                </form>
              </ModalBody>

              <ModalFooter gap={2}>
                <Button className="btn clicky" onClick={onClose}>
                  Batal
                </Button>

                <Button
                  type="submit"
                  form="tambahCourseForm"
                  colorScheme="p"
                  className="clicky"
                  isLoading={loading}
                >
                  Simpan & Lanjutkan
                </Button>
              </ModalFooter>
            </>
          ) : (
            <ComponentSpinner minH={"400px"} />
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
